import React, { useEffect, useRef, useState } from 'react';
import { CarForm } from './CarForm';
import { CheckCircle2, Banknote, TrendingUp, ShieldCheck, Menu, X, Phone, MapPin, ArrowUp, ArrowRight } from 'lucide-react';
import { ServiceType, getServiceContent, capitalizeCityName } from '../routes/seoRoutes';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../context/AppContext';

// Create a custom event for scrolling to form
export const scrollToFormEvent = new CustomEvent('scrollToForm');

// Create a helper function for scrolling to the form section
export const scrollToFormSection = () => {
  const formSection = document.getElementById('quote-section');
  if (formSection) {
    formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

export const Hero: React.FC = () => {
  const { service, city } = useAppContext() || {};
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    scrollToFormSection();
    setIsMenuOpen(false);
  };

  // Listen for scroll to form events
  useEffect(() => {
    const handleScrollToForm = () => scrollToForm();
    window.addEventListener('scrollToForm', handleScrollToForm);
    return () => window.removeEventListener('scrollToForm', handleScrollToForm);
  }, []);

  // Lock scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get content using centralized function
  const content = service 
    ? getServiceContent(service, city) 
    : getServiceContent('valuta-la-tua-auto'); // Default service

  const getFeatures = (serviceType?: ServiceType) => {
    switch (serviceType) {
      case 'valuta-la-tua-auto':
        return [
          { title: 'Gratuita', subtitle: 'Valutazione', icon: CheckCircle2 },
          { title: 'Immediato', subtitle: 'Preventivo', icon: Banknote },
          { title: 'Miglior', subtitle: 'Prezzo', icon: TrendingUp },
          { title: 'Senza', subtitle: 'Problemi', icon: ShieldCheck }
        ];
      
      case 'vendi-la-tua-auto':
        return [
          { title: 'In Giornata', subtitle: 'Pagamento', icon: CheckCircle2 },
          { title: 'Gratuito', subtitle: 'Ritiro', icon: Banknote },
          { title: 'Miglior', subtitle: 'Prezzo', icon: TrendingUp },
          { title: 'Zero', subtitle: 'Scartoffie', icon: ShieldCheck }
        ];

      case 'acquisto-auto-istantaneo':
        return [
          { title: 'Immediata', subtitle: 'Offerta', icon: CheckCircle2 },
          { title: 'Rapido', subtitle: 'Pagamento', icon: Banknote },
          { title: 'Equo', subtitle: 'Prezzo', icon: TrendingUp },
          { title: 'Gratuito', subtitle: 'Ritiro', icon: ShieldCheck }
        ];

      case 'ispezione-pre-acquisto':
        return [
          { title: 'Esperta', subtitle: 'Valutazione', icon: CheckCircle2 },
          { title: 'Dettagliato', subtitle: 'Report', icon: Banknote },
          { title: 'Professionale', subtitle: 'Servizio', icon: TrendingUp },
          { title: 'Qualità', subtitle: 'Garantita', icon: ShieldCheck }
        ];

      case 'valutazione-professionale-dell-auto':
        return [
          { title: 'Esperta', subtitle: 'Valutazione', icon: CheckCircle2 },
          { title: 'Analisi', subtitle: 'di Mercato', icon: Banknote },
          { title: 'Accurato', subtitle: 'Prezzo', icon: TrendingUp },
          { title: 'Dettagliato', subtitle: 'Report', icon: ShieldCheck }
        ];

      case 'vendi-la-tua-auto-ora':
        return [
          { title: 'Immediato', subtitle: 'Preventivo', icon: CheckCircle2 },
          { title: 'In Giornata', subtitle: 'Pagamento', icon: Banknote },
          { title: 'Gratuito', subtitle: 'Ritiro', icon: TrendingUp },
          { title: 'Zero', subtitle: 'Problemi', icon: ShieldCheck }
        ];

      default:
        return [
          { title: 'Gratuita', subtitle: 'Valutazione', icon: CheckCircle2 },
          { title: 'Immediato', subtitle: 'Preventivo', icon: Banknote },
          { title: 'Miglior', subtitle: 'Prezzo', icon: TrendingUp },
          { title: 'Senza', subtitle: 'Problemi', icon: ShieldCheck }
        ];
    }
  };

  const features = getFeatures(service);

  // Update the scroll to CTA event listener
  useEffect(() => {
    const handleScrollToCTA = () => {
      const formSection = document.getElementById('quote-section');
      if (formSection) {
        formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    window.addEventListener('scrollToCTA', handleScrollToCTA);
    return () => window.removeEventListener('scrollToCTA', handleScrollToCTA);
  }, []);
  
  return (
    <>
      {/* Header Navigation */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md"
      >
        <div className="container px-4 mx-auto">
          <div className="flex items-center justify-between h-20">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center space-x-4"
            >
              <img src="/img/logo.png" alt="ValutaAuto" className="h-10" />
            </motion.div>

            {/* Desktop Menu */}
            <nav className="items-center hidden space-x-8 md:flex">
              <motion.a 
                whileHover={{ scale: 1.05 }}
                href="#" 
                onClick={(e) => { e.preventDefault(); scrollToForm(); }}
                className="text-gray-700 transition hover:text-blue-600"
              >
                Home 
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.05 }}
                href="#come-funziona" 
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-700 transition hover:text-blue-600"
              >
                Come Funziona
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.05 }}
                href="#servizi" 
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-700 transition hover:text-blue-600"
              >
                Servizi
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.05 }}
                href="#marche" 
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-700 transition hover:text-blue-600"
              >
                Marche
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.05 }}
                href="#faq" 
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-700 transition hover:text-blue-600"
              >
                FAQ 
              </motion.a>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                onClick={scrollToForm}
                className="px-6 py-2 text-gray-900 transition bg-[#fdc751] rounded-md hover:bg-[#fdc751]/90 font-semibold"
              >
                Valuta ORA
              </motion.button>
            </nav>

            {/* Mobile Menu Button */}
            <motion.button 
              whileTap={{ scale: 0.95 }}
              className="p-2 md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-50 overflow-y-auto bg-white md:hidden" 
              style={{ top: '80px' }}
            >
              <nav className="container px-4 py-6 mx-auto">
                <div className="flex flex-col space-y-4">
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    href="#" 
                    onClick={(e) => { e.preventDefault(); scrollToForm(); }}
                    className="p-3 text-lg text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Home 
                  </motion.a>
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    href="#how-it-works" 
                    onClick={() => setIsMenuOpen(false)}
                    className="p-3 text-lg text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Come Funziona
                  </motion.a>
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    href="#services" 
                    onClick={() => setIsMenuOpen(false)}
                    className="p-3 text-lg text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Servizi
                  </motion.a>
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    href="#brands" 
                    onClick={() => setIsMenuOpen(false)}
                    className="p-3 text-lg text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Marche
                  </motion.a>
                  <motion.a 
                    whileHover={{ scale: 1.05 }}
                    href="#faq" 
                    onClick={() => setIsMenuOpen(false)}
                    className="p-3 text-lg text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    FAQ 
                  </motion.a>
                  
                  {/* Contact Information */}
                  <div className="p-4 mt-4 space-y-3 border-t border-gray-200">
                    <a 
                      href="tel:+393512345678" 
                      className="flex items-center text-gray-600 hover:text-blue-600"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      +39 351 234 5678
                    </a>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-5 h-5 mr-2" />
                      Servizio in tutta Italia
                    </div>
                  </div>
                  
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    onClick={() => {
                      scrollToForm();
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-6 py-3 text-gray-900 transition bg-[#fdc751] rounded-md hover:bg-[#fdc751]/90 font-semibold"
                  >
                    Ottieni un Preventivo Gratuito
                  </motion.button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Hero Section with dynamic content */}
      <section 
        ref={heroRef} 
        id="hero-section"
        className="relative flex items-center w-full min-h-screen pt-20 pb-20 mt-20 bg-center bg-no-repeat bg-cover scroll-mt-20 md:bg-fixed" 
        style={{ 
          backgroundImage: 'url(/img/hero-home.jpg)',
        }}
      >
        {/* Mobile Background Overlay */}
        <div 
          className="absolute inset-0 md:hidden"
          style={{
            backgroundImage: 'url(/img/hero-home.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'left',
            filter: 'brightness(0.5)'
          }}
        />
        {/* Desktop Background Overlay */}
        <div className="absolute inset-0 hidden bg-black opacity-50 md:block" />
        <div className="container relative z-10 w-full px-4 mx-auto">
          <div className="grid items-center w-full grid-cols-1 gap-12 lg:grid-cols-2">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-white"
            >
              <div className="mb-8">
                <motion.span 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="block mb-4 text-lg font-semibold md:text-xl"
                >
                  {content.subtitle}
                </motion.span>
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mb-6 text-3xl font-bold md:text-5xl"
                >
                  {content.title}
                </motion.h1>
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mb-8 text-lg text-gray-200 md:text-xl"
                >
                  {content.description}
                </motion.p>
                
                {/* Features Grid with dynamic content */}
                <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 md:gap-8 md:mb-12">
                  {features.map((feature, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center p-4 space-x-4 transition-all rounded-lg bg-white/10 backdrop-blur-sm hover:bg-white/20"
                    >
                      <feature.icon className="w-6 h-6 text-blue-400 md:w-8 md:h-8" />
                      <div>
                        <p className="text-base font-medium md:text-lg">{feature.title}</p>
                        <p className="text-sm text-gray-300 md:text-base">{feature.subtitle}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4"
              >
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={scrollToForm}
                  className="px-6 py-3 text-gray-900 transition bg-[#fdc751] rounded-md md:px-8 hover:bg-[#fdc751]/90 font-semibold flex items-center justify-center"
                >
                  <TrendingUp className="w-5 h-5 mr-2 md:w-6 md:h-6" />
                  {service && city ? `Valuta la tua auto a ${capitalizeCityName(city)}` : 'Valuta la tua auto'}
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const targetSection = document.getElementById('come-funziona');
                    if (targetSection) {
                      targetSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="flex items-center justify-center px-6 py-3 text-[#fdc751] transition bg-white rounded-md md:px-8 hover:bg-gray-100 font-semibold"
                >
                  Scopri di Più
                  <ArrowRight className="w-3 h-3 ml-2" />
                </motion.button>  
              </motion.div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="p-4 bg-white rounded-lg shadow-xl md:p-8"
            >
              <CarForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={scrollToForm}
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 flex items-center px-6 py-3 space-x-3 text-gray-900 transition bg-[#fdc751] rounded-full shadow-lg hover:bg-[#fdc751]/90 group font-semibold"
          >
            <span className="text-base font-medium">Valuta ORA</span>
            <ArrowUp className="w-5 h-5 transition-transform group-hover:-translate-y-1" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};